import type { NavItemIdentifier } from 'src/types/nav-favorites';
import type { NavSectionProps, NavItemDataProps } from 'src/components/nav-section';

import { useMemo } from 'react';

import Box from '@mui/material/Box';

import { paths } from 'src/routes/paths';

import { useSidebarCounts } from 'src/hooks/use-sidebar-counts';
import { useListiclesPendingCount } from 'src/hooks/useListicles';
import { useAgentKbGapCount } from 'src/hooks/useAgentKbGapCount';
import { useReputationNewCount } from 'src/hooks/useReputationNewCount';
import { useAgentSupportOpenCount } from 'src/hooks/useAgentSupportOpenCount';

import { CONFIG } from 'src/global-config';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { SvgColor } from 'src/components/svg-color';

import { WorkspaceEnum } from 'src/types/user';

// ----------------------------------------------------------------------

const icon = (name: string) => (
  <SvgColor src={`${CONFIG.assetsDir}/assets/icons/navbar/${name}.svg`} />
);

// Badge autónomo con el nº de solicitudes de listicles pendientes de gestionar.
function ListiclesNavBadge() {
  const { count } = useListiclesPendingCount();
  if (!count) return null;
  return (
    <Label color="error" variant="inverted">
      {count}
    </Label>
  );
}

// Badge autónomo con el nº de peticiones de soporte del asistente abiertas.
function AgentSupportNavBadge() {
  const { count } = useAgentSupportOpenCount();
  if (!count) return null;
  return (
    <Label color="error" variant="inverted">
      {count}
    </Label>
  );
}

// Badge autónomo con el nº de huecos de conocimiento (preguntas con voto negativo).
function AgentKbGapNavBadge() {
  const { count } = useAgentKbGapCount();
  if (!count) return null;
  return (
    <Label color="warning" variant="inverted">
      {count}
    </Label>
  );
}

// Badges del padre "Agente IA": agrupa soporte + huecos para que los contadores
// sigan viéndose cuando el desplegable está cerrado.
function AgentNavBadges() {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <AgentSupportNavBadge />
      <AgentKbGapNavBadge />
    </Box>
  );
}

// Badge autónomo con el nº de solicitudes de reputación nuevas (activas).
function ReputationNavBadge() {
  const { count } = useReputationNewCount();
  if (!count) return null;
  return (
    <Label color="error" variant="inverted">
      {count}
    </Label>
  );
}

const ICONS = {
  job: icon('ic-job'),
  blog: icon('ic-blog'),
  chat: icon('ic-chat'),
  mail: icon('ic-mail'),
  user: icon('ic-user'),
  file: icon('ic-file'),
  lock: icon('ic-lock'),
  tour: icon('ic-tour'),
  order: icon('ic-order'),
  label: icon('ic-label'),
  blank: icon('ic-blank'),
  kanban: icon('ic-kanban'),
  folder: icon('ic-folder'),
  course: icon('ic-course'),
  banking: icon('ic-banking'),
  booking: icon('ic-booking'),
  invoice: icon('ic-invoice'),
  product: icon('ic-product'),
  calendar: icon('ic-calendar'),
  disabled: icon('ic-disabled'),
  external: icon('ic-external'),
  menuItem: icon('ic-menu-item'),
  ecommerce: icon('ic-ecommerce'),
  analytics: icon('ic-analytics'),
  dashboard: icon('ic-dashboard'),
  parameter: icon('ic-parameter'),
  medias: icon('ic-medias'),
  tools: icon('ic-parameter'),
  banners: icon('ic-banner'),
  editors: icon('ic-user'),
};

// ----------------------------------------------------------------------

// Configuración unificada del menú con roles permitidos para cada item
const getUnifiedNavData = (
  count: number | null,
  loading: boolean,
  pendingOrdersCount: number | null,
  pendingOrdersLoading: boolean,
  pendingJournalistsCount: number | null,
  pendingJournalistsLoading: boolean,
  pendingMediasCount: number | null,
  pendingMediasLoading: boolean,
  pendingPaymentRequestsCount: number | null,
  pendingPaymentRequestsLoading: boolean,
  pendingNewsletterAdsRequestsCount: number | null,
  pendingNewsletterAdsRequestsLoading: boolean,
  pendingOutreachCount: number | null,
  pendingOutreachLoading: boolean,
  pendingMessagesCount: number | null,
  pendingMessagesLoading: boolean,
  pendingManagedServicesCount: number | null,
  pendingManagedServicesLoading: boolean,
  pendingKiwosanMediaCount: number | null = null,
  pendingKiwosanMediaLoading: boolean = false
): NavSectionProps['data'] => [
  {
    subheader: 'GENERAL',
    allowedRoles: [
      WorkspaceEnum.Administrator,
      WorkspaceEnum.Accountant,
      WorkspaceEnum.EditorBoss,
      WorkspaceEnum.OperationsManager,
      WorkspaceEnum.Supervisor,
      WorkspaceEnum.Outreach,
    ],
    items: [
      {
        title: 'Dashboard',
        path: paths.dashboard.root,
        icon: ICONS.dashboard,
        allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.Accountant],
      },
      {
        title: 'Estadísticas',
        path: paths.dashboard.statistics.root,
        icon: ICONS.analytics,
        allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.Accountant],
        children: [
          {
            title: 'Facturación',
            path: paths.dashboard.statistics.billing,
            allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.Accountant],
          },
          {
            title: 'Rendimiento',
            path: paths.dashboard.statistics.performance,
            allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.Accountant],
          },
          {
            title: 'Producto',
            path: paths.dashboard.statistics.product,
            allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.Accountant],
          },
          {
            title: 'Cliente',
            path: paths.dashboard.statistics.customer,
            allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.Accountant],
          },
          {
            title: 'Editores',
            path: paths.dashboard.statistics.editors,
            allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.Accountant],
          },
        ],
      },
      {
        title: 'Clientes',
        path: paths.dashboard.customer.list,
        icon: ICONS.user,
        allowedRoles: [
          WorkspaceEnum.Administrator,
          WorkspaceEnum.OperationsManager,
          WorkspaceEnum.Supervisor,
          WorkspaceEnum.Accountant,
        ],
      },
      {
        title: 'Usuarios',
        path: paths.dashboard.adminUsers.root,
        icon: ICONS.user,
        // Solo Administrator y Supervisor gestionan usuarios del staff (Outreach fuera).
        // Ambos crean/editan; borrar y cambiar contrasena quedan solo para Administrator
        // (el backend lo exige y el front oculta esas dos acciones a Supervisor).
        allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.OperationsManager, WorkspaceEnum.Supervisor],
      },
      {
        title: 'Periodistas pendientes',
        path: paths.dashboard.pendingJournalists.root,
        icon: ICONS.user,
        allowedRoles: [WorkspaceEnum.Administrator],
        info:
          !pendingJournalistsLoading &&
          pendingJournalistsCount != null &&
          pendingJournalistsCount > 0 ? (
            <Label color="error" variant="inverted">
              {pendingJournalistsCount}
            </Label>
          ) : null,
      },
      {
        title: 'Tracker en vivo',
        path: paths.dashboard.liveTracker.root,
        icon: <Iconify icon="eva:activity-fill" width={24} />,
        allowedRoles: [WorkspaceEnum.Administrator],
      },
      {
        title: 'Logs',
        path: paths.dashboard.logs.root,
        icon: ICONS.label,
        allowedRoles: [WorkspaceEnum.Administrator],
      },
      {
        // Productos SaaS de Getalink One agrupados en un único desplegable.
        title: 'Getalink One',
        path: paths.dashboard.fastIndexer.root,
        icon: <Iconify icon="carbon:rocket" width={24} />,
        allowedRoles: [WorkspaceEnum.Administrator],
        children: [
          {
            title: 'Indexador',
            path: paths.dashboard.fastIndexer.root,
            allowedRoles: [WorkspaceEnum.Administrator],
          },
          {
            title: 'Backlink Monitor',
            path: paths.dashboard.backlinkMonitor.root,
            allowedRoles: [WorkspaceEnum.Administrator],
            children: [
              {
                title: 'Resumen',
                path: paths.dashboard.backlinkMonitor.root,
                allowedRoles: [WorkspaceEnum.Administrator],
              },
              {
                title: 'Estado técnico',
                path: paths.dashboard.backlinkMonitor.technical,
                allowedRoles: [WorkspaceEnum.Administrator],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    subheader: 'Gestión',
    allowedRoles: [
      WorkspaceEnum.Administrator,
      WorkspaceEnum.OperationsManager,
      WorkspaceEnum.Supervisor,
      WorkspaceEnum.EditorBoss,
      WorkspaceEnum.Editors,
      WorkspaceEnum.Journalists,
      WorkspaceEnum.Outreach,
      WorkspaceEnum.Accountant,
    ],
    items: [
      {
        title: 'Pedidos',
        path: paths.dashboard.order.root,
        icon: ICONS.order,
        allowedRoles: [
          WorkspaceEnum.Administrator,
          WorkspaceEnum.OperationsManager,
          WorkspaceEnum.Supervisor,
          WorkspaceEnum.EditorBoss,
        ],
        info:
          !pendingOrdersLoading && pendingOrdersCount != null && pendingOrdersCount > 0 ? (
            <Label color="error" variant="inverted">
              {pendingOrdersCount}
            </Label>
          ) : null,
        children: [
          {
            title: 'Getalink',
            path: paths.dashboard.order.root,
            allowedRoles: [
              WorkspaceEnum.Administrator,
              WorkspaceEnum.OperationsManager,
              WorkspaceEnum.Supervisor,
              WorkspaceEnum.EditorBoss,
            ],
          },
          {
            title: 'Externos',
            path: paths.dashboard.order.externals.root,
            allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.OperationsManager, WorkspaceEnum.Supervisor],
            children: [
              {
                title: 'Lista',
                path: paths.dashboard.order.externals.root,
                allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.OperationsManager, WorkspaceEnum.Supervisor],
              },
              {
                title: 'Pendientes a cobrar',
                path: paths.dashboard.order.externals.pendingPayments,
                allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.OperationsManager, WorkspaceEnum.Supervisor],
                info:
                  !loading && count != null ? (
                    <Label color="error" variant="inverted">
                      {count}
                    </Label>
                  ) : (
                    <Label color="warning" variant="inverted">
                      +2
                    </Label>
                  ),
              },
            ],
          },
        ],
      },
      {
        title: 'Pedidos Asignados',
        path: paths.dashboard.order.root,
        icon: ICONS.order,
        allowedRoles: [WorkspaceEnum.Editors, WorkspaceEnum.Journalists],
      },
      {
        title: 'Medios',
        icon: ICONS.file,
        path: paths.dashboard.medias.root,
        allowedRoles: [
          WorkspaceEnum.Administrator,
          WorkspaceEnum.OperationsManager,
          WorkspaceEnum.Supervisor,
          WorkspaceEnum.Outreach,
        ],
        children: [
          {
            title: 'Lista',
            path: paths.dashboard.medias.root,
            allowedRoles: [
              WorkspaceEnum.Administrator,
              WorkspaceEnum.OperationsManager,
              WorkspaceEnum.Supervisor,
              WorkspaceEnum.Outreach,
            ],
          },
          {
            title: 'Categorías',
            path: paths.dashboard.medias.categories,
            allowedRoles: [
              WorkspaceEnum.Administrator,
              WorkspaceEnum.OperationsManager,
              WorkspaceEnum.Supervisor,
              WorkspaceEnum.Outreach,
            ],
          },
          {
            title: 'Packs de medios',
            path: paths.dashboard.medias.packs.root,
            allowedRoles: [WorkspaceEnum.Administrator],
          },
          {
            title: 'Importar Medios',
            path: paths.dashboard.medias.importMedia,
            allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.OperationsManager, WorkspaceEnum.Supervisor],
          },
        ],
      },
      {
        title: 'Servicios',
        icon: ICONS.parameter,
        path: paths.dashboard.managedServices.root,
        allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.OperationsManager, WorkspaceEnum.Supervisor],
        info:
          !pendingManagedServicesLoading &&
          pendingManagedServicesCount != null &&
          pendingManagedServicesCount > 0 ? (
            <Label color="error" variant="inverted">
              {pendingManagedServicesCount}
            </Label>
          ) : null,
        children: [
          {
            title: 'Servicios gestionados',
            path: paths.dashboard.managedServices.root,
            allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.OperationsManager, WorkspaceEnum.Supervisor],
            info:
              !pendingManagedServicesLoading &&
              pendingManagedServicesCount != null &&
              pendingManagedServicesCount > 0 ? (
                <Label color="error" variant="inverted">
                  {pendingManagedServicesCount}
                </Label>
              ) : null,
          },
        ],
      },
      {
        title: 'Vitalinks',
        path: paths.dashboard.vitalinks.root,
        icon: ICONS.external,
        allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.OperationsManager, WorkspaceEnum.Supervisor],
      },
      {
        title: 'Listicles',
        path: paths.dashboard.listicles.root,
        icon: ICONS.analytics,
        allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.OperationsManager, WorkspaceEnum.Supervisor],
        info: <ListiclesNavBadge />,
      },
      {
        title: 'Agente IA',
        path: paths.dashboard.agenteIa.root,
        icon: ICONS.chat,
        allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.OperationsManager, WorkspaceEnum.Supervisor],
        // Los contadores de soporte y huecos también se muestran en el padre para
        // que sigan siendo visibles con el desplegable cerrado.
        info: <AgentNavBadges />,
        children: [
          {
            title: 'Base de conocimiento',
            path: paths.dashboard.agenteIa.root,
            allowedRoles: [WorkspaceEnum.Administrator],
          },
          {
            title: 'Logs de usuarios',
            path: paths.dashboard.agenteIa.logs,
            allowedRoles: [WorkspaceEnum.Administrator],
          },
          {
            title: 'Soporte del asistente',
            path: paths.dashboard.agenteIa.support,
            allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.OperationsManager, WorkspaceEnum.Supervisor],
            info: <AgentSupportNavBadge />,
          },
          {
            title: 'Huecos de conocimiento',
            path: paths.dashboard.agenteIa.gaps,
            allowedRoles: [WorkspaceEnum.Administrator],
            info: <AgentKbGapNavBadge />,
          },
          {
            title: 'Control de calidad',
            path: paths.dashboard.agenteIa.evals,
            allowedRoles: [WorkspaceEnum.Administrator],
          },
        ],
      },
      {
        title: 'Facturación',
        path: paths.dashboard.billing.platforms.root,
        icon: ICONS.banking,
        allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.Accountant],
        info:
          !pendingPaymentRequestsLoading &&
          pendingPaymentRequestsCount &&
          pendingPaymentRequestsCount > 0 ? (
            <Label color="error" variant="inverted">
              {pendingPaymentRequestsCount}
            </Label>
          ) : null,
        children: [
          {
            title: 'Lista',
            path: paths.dashboard.invoices.root,
            allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.Accountant],
          },
          {
            title: 'Facturación LLC',
            path: paths.dashboard.invoices.llc,
            allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.Accountant],
          },
          {
            title: 'Gastos externos',
            path: paths.dashboard.invoices.expenses.root,
            // Facturación: el Supervisor queda fuera. El Director de operaciones tiene su entrada suelta.
            allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.Accountant],
          },
          {
            title: 'Solicitudes de pago',
            path: paths.dashboard.invoices.paymentRequests.root,
            // Facturación: el Supervisor queda fuera. El Director de operaciones tiene su entrada suelta.
            allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.Accountant],
            info:
              !pendingPaymentRequestsLoading &&
              pendingPaymentRequestsCount &&
              pendingPaymentRequestsCount > 0 ? (
                <Label color="error" variant="inverted">
                  {pendingPaymentRequestsCount}
                </Label>
              ) : null,
          },
          {
            title: 'Plataformas',
            path: paths.dashboard.billing.platforms.root,
            allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.Accountant],
          },
        ],
      },
      // Gastos externos y Solicitudes de pago sueltos para el Director de operaciones: no ve el grupo
      // Facturación (facturas, LLC, plataformas), pero genera gastos y tramita solicitudes de pago
      // (Aitor, 07/10). El Supervisor queda fuera de facturación.
      {
        title: 'Gastos externos',
        path: paths.dashboard.invoices.expenses.root,
        icon: ICONS.banking,
        allowedRoles: [WorkspaceEnum.OperationsManager],
      },
      {
        title: 'Solicitudes de pago',
        path: paths.dashboard.invoices.paymentRequests.root,
        icon: ICONS.banking,
        allowedRoles: [WorkspaceEnum.OperationsManager],
        info:
          !pendingPaymentRequestsLoading &&
          pendingPaymentRequestsCount &&
          pendingPaymentRequestsCount > 0 ? (
            <Label color="error" variant="inverted">
              {pendingPaymentRequestsCount}
            </Label>
          ) : null,
      },
      {
        title: 'Outreach',
        path: paths.dashboard.outreach.root,
        icon: ICONS.external,
        allowedRoles: [
          WorkspaceEnum.Administrator,
          WorkspaceEnum.OperationsManager,
          WorkspaceEnum.Supervisor,
          WorkspaceEnum.Outreach,
        ],
        info:
          !pendingOutreachLoading && pendingOutreachCount != null && pendingOutreachCount > 0 ? (
            <Label color="error" variant="inverted">
              {pendingOutreachCount}
            </Label>
          ) : null,
        children: [
          { title: 'Solicitudes', path: paths.dashboard.outreach.root },
          {
            title: 'Extensión navegador',
            path: paths.dashboard.outreach.extension,
            // Outreach no opera aquí (su trabajo está en Solicitudes); solo Admin + Supervisor.
            allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.OperationsManager, WorkspaceEnum.Supervisor],
          },
        ],
      },
      {
        title: 'Medios kiwosan',
        path: paths.dashboard.kiwosanMedia.root,
        icon: ICONS.external,
        allowedRoles: [
          WorkspaceEnum.Administrator,
          WorkspaceEnum.OperationsManager,
          WorkspaceEnum.Supervisor,
          WorkspaceEnum.Outreach,
        ],
        info:
          !pendingKiwosanMediaLoading &&
          pendingKiwosanMediaCount != null &&
          pendingKiwosanMediaCount > 0 ? (
            <Label color="error" variant="inverted">
              {pendingKiwosanMediaCount}
            </Label>
          ) : null,
      },
      {
        title: 'Media Discovery',
        path: paths.dashboard.outreachDiscovery.root,
        icon: ICONS.external,
        allowedRoles: [
          WorkspaceEnum.Administrator,
          WorkspaceEnum.OperationsManager,
          WorkspaceEnum.Supervisor,
          WorkspaceEnum.Outreach,
        ],
        children: [
          { title: 'Campañas', path: paths.dashboard.outreachDiscovery.root },
          { title: 'Outreach interno', path: paths.dashboard.outreachDiscovery.internalOutreach },
          { title: 'Plantillas', path: paths.dashboard.outreachDiscovery.templates },
          { title: 'Supresiones', path: paths.dashboard.outreachDiscovery.suppressions },
          { title: 'Estado técnico', path: paths.dashboard.outreachDiscovery.technical },
        ],
      },
      {
        title: 'Gestión de reputación',
        path: paths.dashboard.reputation.root,
        icon: ICONS.label,
        allowedRoles: [WorkspaceEnum.Administrator],
        info: <ReputationNavBadge />,
      },
      {
        title: 'Herramientas',
        path: paths.dashboard.tools.root,
        icon: ICONS.tools,
        allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.OperationsManager, WorkspaceEnum.Supervisor],
        children: [
          {
            title: 'Cupones',
            path: paths.dashboard.tools.coupons.root,
            allowedRoles: [WorkspaceEnum.Administrator],
          },
          {
            title: 'Calendario',
            path: paths.dashboard.tools.calendar.root,
            allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.OperationsManager, WorkspaceEnum.Supervisor],
          },
          {
            title: 'Propuestas de medios',
            path: paths.dashboard.tools.mediaProposals.root,
            allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.OperationsManager, WorkspaceEnum.Supervisor],
          },
          {
            title: 'Gestión de Profits',
            path: paths.dashboard.tools.profitManagement.root,
            allowedRoles: [WorkspaceEnum.Administrator],
          },
          {
            title: 'Banners',
            path: paths.dashboard.banners.root,
            allowedRoles: [WorkspaceEnum.Administrator],
          },
          {
            title: 'Alertas',
            path: paths.dashboard.alertas.root,
            allowedRoles: [WorkspaceEnum.Administrator],
          },
          {
            title: 'Mailgun',
            path: paths.dashboard.tools.mailgun.root,
            allowedRoles: [WorkspaceEnum.Administrator],
          },
          {
            title: 'Analizador de Categorías',
            path: paths.dashboard.tools.categoryAnalyzer.root,
            allowedRoles: [WorkspaceEnum.Administrator],
          },
          {
            title: 'API',
            path: paths.dashboard.tools.apiKeys.root,
            allowedRoles: [WorkspaceEnum.Administrator],
          },
          {
            title: 'API Comparadores',
            path: paths.dashboard.tools.comparatorApiKeys.root,
            allowedRoles: [WorkspaceEnum.Administrator],
          },
          {
            title: 'Idiomas de comunicación',
            path: paths.dashboard.tools.languageConfig.root,
            allowedRoles: [WorkspaceEnum.Administrator],
          },
          {
            title: 'Status backups',
            path: paths.dashboard.backups.root,
            allowedRoles: [WorkspaceEnum.Administrator],
          },
          {
            title: 'Hangfire',
            path: paths.dashboard.hangfire.root,
            allowedRoles: [WorkspaceEnum.Administrator],
          },
          {
            title: 'Salud del Sistema',
            path: paths.dashboard.systemHealth.root,
            allowedRoles: [WorkspaceEnum.Administrator],
          },
          {
            title: 'Respuestas rápidas',
            path: paths.dashboard.quickReplyTemplates.root,
            allowedRoles: [WorkspaceEnum.Administrator],
          },
        ],
      },
    ],
  },
  {
    subheader: 'Gestión editores',
    allowedRoles: [WorkspaceEnum.Administrator],
    items: [
      {
        title: 'Medios pendientes',
        path: paths.dashboard.editorManagement.pendingMedias,
        icon: ICONS.editors,
        allowedRoles: [WorkspaceEnum.Administrator],
        info:
          !pendingMediasLoading && pendingMediasCount != null && pendingMediasCount > 0 ? (
            <Label color="error" variant="inverted">
              {pendingMediasCount}
            </Label>
          ) : null,
      },
      {
        title: 'Newsletters',
        path: paths.dashboard.editorManagement.newsletters.root,
        icon: ICONS.mail,
        allowedRoles: [WorkspaceEnum.Administrator],
        info:
          !pendingNewsletterAdsRequestsLoading &&
          pendingNewsletterAdsRequestsCount != null &&
          pendingNewsletterAdsRequestsCount > 0 ? (
            <Label color="error" variant="inverted">
              {pendingNewsletterAdsRequestsCount}
            </Label>
          ) : null,
        children: [
          {
            title: 'Gestión',
            path: paths.dashboard.editorManagement.newsletters.root,
            allowedRoles: [WorkspaceEnum.Administrator],
          },
          {
            title: 'Ads Requests',
            path: paths.dashboard.tools.newsletterAdsRequests.root,
            allowedRoles: [WorkspaceEnum.Administrator],
            info:
              !pendingNewsletterAdsRequestsLoading &&
              pendingNewsletterAdsRequestsCount != null &&
              pendingNewsletterAdsRequestsCount > 0 ? (
                <Label color="error" variant="inverted">
                  {pendingNewsletterAdsRequestsCount}
                </Label>
              ) : null,
          },
          {
            title: 'Catálogo',
            path: paths.dashboard.tools.newsletterCatalog.root,
            allowedRoles: [WorkspaceEnum.Administrator],
          },
        ],
      },
    ],
  },
  {
    subheader: 'Moderación',
    allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.OperationsManager, WorkspaceEnum.Supervisor],
    items: [
      {
        title: 'Mensajes a moderar',
        path: paths.dashboard.moderation.messages,
        icon: ICONS.chat,
        allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.OperationsManager, WorkspaceEnum.Supervisor],
        info:
          !pendingMessagesLoading && pendingMessagesCount != null && pendingMessagesCount > 0 ? (
            <Label color="error" variant="inverted">
              {pendingMessagesCount}
            </Label>
          ) : null,
      },
      {
        title: 'Palabras prohibidas',
        path: paths.dashboard.moderation.bannedWords,
        icon: ICONS.lock,
        allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.OperationsManager, WorkspaceEnum.Supervisor],
      },
    ],
  },
  {
    subheader: 'IA / MCP',
    allowedRoles: [WorkspaceEnum.Administrator, WorkspaceEnum.OperationsManager, WorkspaceEnum.Supervisor],
    items: [
      {
        title: 'Restricciones del MCP',
        path: paths.dashboard.mcp.restrictions,
        icon: ICONS.tools,
        // El backend es solo Administrador; se quita Supervisor del menú para que no dé 403.
        allowedRoles: [WorkspaceEnum.Administrator],
      },
    ],
  },
];

// Hook para usar favoritos con el menú de navegación
export function useNavDataWithFavorites(
  currentWorkspace?: WorkspaceEnum,
  favorites?: NavItemIdentifier[]
): NavSectionProps['data'] {
  const { counts, loading } = useSidebarCounts();

  // Obtener el menú unificado
  const fullNavData = getUnifiedNavData(
    counts?.pendingPayments ?? null,
    loading,
    counts?.pendingOrders ?? null,
    loading,
    counts?.pendingJournalists ?? null,
    loading,
    counts?.pendingMedias ?? null,
    loading,
    counts?.pendingEditorRequests ?? null,
    loading,
    counts?.pendingNewsletterAdsRequests ?? null,
    loading,
    counts?.pendingOutreach ?? null,
    loading,
    counts?.messages?.totalPending ?? null,
    loading,
    counts?.pendingManagedServices ?? null,
    loading,
    counts?.pendingKiwosanMedia ?? null,
    loading
  );

  // Función para encontrar un item por su path en el menú completo
  const findItemByPath = (
    sections: NavSectionProps['data'],
    targetPath: string,
    parentPath?: string
  ): NavItemDataProps | null => {
    for (const section of sections) {
      for (const item of section.items) {
        if (item.path === targetPath && !parentPath) {
          return item;
        }
        if (item.children) {
          for (const child of item.children) {
            if (child.path === targetPath && item.path === parentPath) {
              return child;
            }
            // Búsqueda recursiva para sub-sub-items
            if (child.children) {
              const found = child.children.find((subChild) => subChild.path === targetPath);
              if (found) return found;
            }
          }
        }
      }
    }
    return null;
  };

  // Filtrar los items basados en el workspace actual
  const filterNavItems = (items: any[], excludeFavorites = false): any[] =>
    items
      .filter((item) => {
        // Si no hay allowedRoles, mostrar a todos
        if (!item.allowedRoles || item.allowedRoles.length === 0) return true;

        // Si no hay workspace actual, usar Administrator por defecto
        const workspace = currentWorkspace || WorkspaceEnum.Administrator;

        // Verificar si el workspace actual está en los roles permitidos
        return item.allowedRoles.includes(workspace);
      })
      .filter((item) => {
        // Si excludeFavorites es true y tenemos favoritos, excluir los items que están en favoritos
        if (excludeFavorites && favorites && favorites.length > 0) {
          const isFavorite = favorites.some((fav) => fav.path === item.path && !fav.parentPath);
          if (isFavorite) return false;
        }
        return true;
      })
      .map((item) => ({
        ...item,
        // Filtrar recursivamente los hijos si existen
        children: item.children
          ? filterNavItems(item.children, excludeFavorites).filter((child: any) => {
              // Excluir hijos que son favoritos
              if (excludeFavorites && favorites && favorites.length > 0) {
                const isFavorite = favorites.some(
                  (fav) => fav.path === child.path && fav.parentPath === item.path
                );
                return !isFavorite;
              }
              return true;
            })
          : undefined,
      }))
      .filter((item) => {
        // Eliminar items que tenían hijos pero ahora no tienen ninguno visible
        if (item.children && item.children.length === 0) {
          // Si es un item sin hijos visibles pero tiene path propio, mantenerlo sin hijos
          return item.path ? { ...item, children: undefined } : false;
        }
        return true;
      });

  // Crear la sección de favoritos si hay favoritos
  const favoritesSection = useMemo(() => {
    if (!favorites || favorites.length === 0) return null;

    const favoriteItems: NavItemDataProps[] = [];

    favorites.forEach((fav) => {
      const item = findItemByPath(fullNavData, fav.path, fav.parentPath);
      if (item) {
        // Verificar que el item es visible para el workspace actual
        const workspace = currentWorkspace || WorkspaceEnum.Administrator;
        if (!item.allowedRoles || item.allowedRoles.includes(workspace)) {
          favoriteItems.push({
            ...item,
            favoriteParentPath: fav.parentPath,
            // Si es un item con hijos, mantenerlos
            children: item.children ? filterNavItems(item.children, false) : undefined,
          });
        }
      }
    });

    if (favoriteItems.length === 0) return null;

    return {
      subheader: 'FAVORITOS',
      items: favoriteItems,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favorites, fullNavData, currentWorkspace]);

  // Filtrar las secciones y sus items (excluyendo favoritos de su posición original)
  const filteredNavData = fullNavData
    .filter((section) => {
      // Filtrar secciones basadas en los roles permitidos del subheader
      if (section.allowedRoles) {
        const workspace = currentWorkspace || WorkspaceEnum.Administrator;
        const allowedRoles = Array.isArray(section.allowedRoles)
          ? section.allowedRoles
          : [section.allowedRoles];
        return allowedRoles.includes(workspace);
      }
      return true; // Si no hay allowedRoles en el subheader, mostrar a todos
    })
    .map((section) => ({
      ...section,
      items: filterNavItems(section.items, true),
    }))
    .filter((section) => section.items.length > 0); // Eliminar secciones vacías

  // Combinar favoritos al principio si existen
  const finalNavData = favoritesSection ? [favoritesSection, ...filteredNavData] : filteredNavData;

  return finalNavData;
}

// Hook principal que retorna la configuración según el workspace (sin favoritos para compatibilidad)
export function useNavData(currentWorkspace?: WorkspaceEnum): NavSectionProps['data'] {
  return useNavDataWithFavorites(currentWorkspace, []);
}

// Keep the static version for backward compatibility or initial rendering
export const navData: NavSectionProps['data'] = [
  {
    subheader: 'GENERAL',
    items: [
      {
        title: 'Dashboard',
        path: paths.dashboard.root,
        icon: ICONS.dashboard,
      },
    ],
  },
  {
    subheader: 'Gestión',
    items: [
      {
        title: 'Pedidos',
        path: paths.dashboard.order.root,
        icon: ICONS.order,
      },
    ],
  },
];
